import { gql } from "@apollo/client";
import {
  DataConnectionId,
  DataSourceFilter,
  OrgRole,
  convertDeprecatedDataSourceFilter,
  guardNever,
} from "@hex/common";
import { useMemo } from "react";

import { useCurrentUser } from "../../../../hooks/me/useCurrentUser";
import { ORG_ID } from "../../../../orgs";
import { useHexFlag } from "../../../../util/useHexFlags";

import {
  useGetThreadsSettingsForEditorQuery,
  useGetThreadsSettingsForExplorerQuery,
} from "./useGetThreadsSettings.generated";

gql`
  query GetThreadsSettingsForExplorer($orgId: OrgId!) {
    orgById(orgId: $orgId) {
      id
      defaultDataConnection {
        id
      }
      exploreAgentSettings {
        id
        explorerEnabled
        explorerDataSource
        explorerDefaultDataConnection {
          id
        }
      }
    }
  }

  query GetThreadsSettingsForEditor($orgId: OrgId!) {
    orgById(orgId: $orgId) {
      id
      defaultDataConnection {
        id
      }
      exploreAgentSettings {
        id
        editorEnabled
        editorDataSource
        editorDefaultDataConnection {
          id
        }
      }
    }
  }
`;

const ROLE_MAP: Record<OrgRole, "EDITOR" | "EXPLORER" | null> = {
  [OrgRole.ADMIN]: OrgRole.EDITOR,
  [OrgRole.MANAGER]: OrgRole.EDITOR,
  [OrgRole.EDITOR]: OrgRole.EDITOR,
  [OrgRole.EXPLORER]: OrgRole.EXPLORER,
  [OrgRole.MEMBER]: null,
  [OrgRole.GUEST]: null,
  [OrgRole.EMBEDDED_USER]: null,
  [OrgRole.ANONYMOUS]: null,
};

export interface ThreadsSettings {
  enabledForRole: boolean;
  dataSource: DataSourceFilter;
  defaultDataConnectionId: DataConnectionId | null;
  orgConfiguredDefaultDataConnectionId: DataConnectionId | null;
}

export const useGetThreadsSettings = (): {
  loading: boolean;
  settings: ThreadsSettings | null;
  minimumRoleForAccess: OrgRole | undefined;
} => {
  const user = useCurrentUser();
  const orgRole = user?.orgRole;

  const settingToLoad = orgRole ? ROLE_MAP[orgRole] : undefined;

  const { data: explorerSettingsData, loading: explorerSettingsLoading } =
    useGetThreadsSettingsForExplorerQuery({
      variables: { orgId: ORG_ID },
      skip: settingToLoad !== "EXPLORER",
      // A permissions error can occur if the explorer doesn't have access to the set
      // default data connection. If that's the case, we want to still use the
      // rest of the data returned.
      errorPolicy: "all",
    });

  const { data: editorSettingsData, loading: editorSettingsLoading } =
    useGetThreadsSettingsForEditorQuery({
      variables: { orgId: ORG_ID },
      skip: settingToLoad !== "EDITOR",
      errorPolicy: "all",
    });

  const endorsedModeFlagValue = useHexFlag("endorsed-mode-threads-setting");

  const result = useMemo(() => {
    const isLegacyMode = endorsedModeFlagValue === "legacy";
    const isEndorsedMode = endorsedModeFlagValue === "endorsed-mode";
    const isStandaloneExplorerToggle =
      endorsedModeFlagValue === "standalone-explorer-toggle";

    // Calculate minimumRoleForAccess based on feature flag value
    const minimumRoleForAccess = (() => {
      if (isEndorsedMode) {
        // In endorsed mode, all Editors and Explorers have access
        // (if the explorer role is enabled for this org)
        return user?.org?.explorerEnabled ? OrgRole.EXPLORER : OrgRole.EDITOR;
      }

      if (isStandaloneExplorerToggle) {
        // Editors always have access; Explorers have access if explorerEnabled
        return explorerSettingsData?.orgById.exploreAgentSettings
          .explorerEnabled
          ? OrgRole.EXPLORER
          : OrgRole.EDITOR;
      }

      // Legacy mode: check role-specific enablement settings
      if (explorerSettingsData?.orgById.exploreAgentSettings.explorerEnabled) {
        return OrgRole.EXPLORER;
      }
      if (editorSettingsData?.orgById.exploreAgentSettings.editorEnabled) {
        return OrgRole.EDITOR;
      }
      return undefined;
    })();

    if (
      orgRole == null ||
      orgRole === OrgRole.EMBEDDED_USER ||
      orgRole === OrgRole.ANONYMOUS ||
      orgRole === OrgRole.MEMBER ||
      orgRole === OrgRole.GUEST
    ) {
      return { loading: false, settings: null, minimumRoleForAccess };
    }

    if (orgRole === OrgRole.ADMIN || orgRole === OrgRole.MANAGER) {
      if (editorSettingsLoading || editorSettingsData == null) {
        return {
          loading: editorSettingsLoading,
          settings: null,
          minimumRoleForAccess,
        };
      }

      const org = editorSettingsData.orgById;
      const settings = org.exploreAgentSettings;
      // In non-legacy mode, use org default data connection only;
      // in legacy mode, fall back through role-specific connection
      const defaultDataConnectionId = isLegacyMode
        ? (settings.editorDefaultDataConnection?.id ??
          org?.defaultDataConnection?.id ??
          null)
        : (org?.defaultDataConnection?.id ?? null);
      const editorDataSource = convertDeprecatedDataSourceFilter(
        settings.editorDataSource,
      );

      return {
        loading: false,
        minimumRoleForAccess,
        settings: {
          enabledForRole: true,
          // Admins can always choose their data source but should default to the
          // editor configuration for consistency with the "restrict agent data
          // access" setting.
          dataSource: editorDataSource,
          defaultDataConnectionId,
          orgConfiguredDefaultDataConnectionId:
            org?.defaultDataConnection?.id ?? null,
        },
      };
    } else if (orgRole === OrgRole.EDITOR) {
      if (editorSettingsLoading || editorSettingsData == null) {
        return {
          loading: editorSettingsLoading,
          settings: null,
          minimumRoleForAccess,
        };
      }

      const org = editorSettingsData.orgById;
      const settings = org.exploreAgentSettings;
      // In non-legacy mode, use org default data connection only;
      // in legacy mode, fall back through role-specific connection
      const defaultDataConnectionId = isLegacyMode
        ? (settings.editorDefaultDataConnection?.id ??
          org?.defaultDataConnection?.id ??
          null)
        : (org?.defaultDataConnection?.id ?? null);

      // In non-legacy mode, editors are always enabled
      const enabledForRole = isLegacyMode ? settings.editorEnabled : true;

      return {
        loading: false,
        minimumRoleForAccess,
        settings: {
          enabledForRole,
          dataSource: convertDeprecatedDataSourceFilter(
            settings.editorDataSource,
          ),
          defaultDataConnectionId,
          orgConfiguredDefaultDataConnectionId:
            org?.defaultDataConnection?.id ?? null,
        },
      };
    } else if (orgRole === OrgRole.EXPLORER) {
      if (explorerSettingsLoading || explorerSettingsData == null) {
        return {
          loading: explorerSettingsLoading,
          settings: null,
          minimumRoleForAccess,
        };
      }

      const org = explorerSettingsData.orgById;
      const settings = org.exploreAgentSettings;
      // In non-legacy mode, use org default data connection only;
      // in legacy mode, fall back through role-specific connection
      const defaultDataConnectionId = isLegacyMode
        ? (settings.explorerDefaultDataConnection?.id ??
          org?.defaultDataConnection?.id ??
          null)
        : (org?.defaultDataConnection?.id ?? null);

      // In endorsed mode, always enabled;
      // in standalone-explorer-toggle, respect explorerEnabled setting;
      // in legacy mode, use the setting as well
      const enabledForRole = isEndorsedMode ? true : settings.explorerEnabled;

      // In non-legacy mode, explorers default to ENDORSED_DATA.
      // The actual data source may change based on org settings (enforceExplorerEndorsedMode)
      // and user preferences - see useThreadsDataSourceFilter for the full logic.
      const dataSource = isLegacyMode
        ? convertDeprecatedDataSourceFilter(settings.explorerDataSource)
        : DataSourceFilter.ENDORSED_DATA;

      return {
        loading: false,
        minimumRoleForAccess,
        settings: {
          enabledForRole,
          dataSource,
          defaultDataConnectionId,
          orgConfiguredDefaultDataConnectionId:
            org?.defaultDataConnection?.id ?? null,
        },
      };
    } else {
      guardNever(orgRole, orgRole);
      return { loading: false, settings: null, minimumRoleForAccess };
    }
  }, [
    explorerSettingsData,
    user?.org?.explorerEnabled,
    endorsedModeFlagValue,
    editorSettingsData,
    orgRole,
    editorSettingsLoading,
    explorerSettingsLoading,
  ]);

  return result;
};
